// src/pages/plan/ReferenceManagement.jsx
import React, { useState } from 'react';
import { Plus, Trash2, Loader2, PenLine, Paperclip } from 'lucide-react';
import { clsx } from 'clsx';
import { useReferencePage } from '@hooks';
import ReferencePostModal from './ReferencePostModal';
import RefCategoryModal from './RefCategoryModal';

export function ReferenceManagement() {
  const {
    isAdmin,
    selectedCatId, setSelectedCatId,
    page, setPage,
    PAGE_SIZE,
    categories, isCategoriesLoading,
    posts, totalCount, isPostsLoading,
    addCategory, deleteCategory,
    createPost, updatePost, deletePost, deletePostFile,
  } = useReferencePage();

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [postModal, setPostModal] = useState({ open: false, mode: 'create', refId: null });

  const handleDeleteCategory = async (catId, e) => {
    e.stopPropagation();
    if (!window.confirm('분류를 삭제하시겠습니까?\n게시글이 있는 분류는 삭제할 수 없습니다.')) return;
    try {
      await deleteCategory(catId);
    } catch (err) {
      alert(err?.response?.data?.message || '삭제 실패');
    }
  };

  const handleDeletePost = async (refId, e) => {
    e.stopPropagation();
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;
    try {
      await deletePost(refId);
    } catch (err) {
      alert('삭제 실패');
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const currentCatName = categories.find(c => c.catId === selectedCatId)?.catName || '자료실';

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-6 animate-in fade-in zoom-in-95 duration-500 w-full max-w-full">

      {/* 좌측 사이드바 */}
      <div className="w-64 shrink-0 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">

        {/* 분류 목록 */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {isCategoriesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : categories.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-6">분류가 없습니다.</p>
          ) : (
            categories.map(cat => (
              <div
                key={cat.catId}
                onClick={() => { setSelectedCatId(cat.catId); setPage(0); }}
                className={clsx(
                  'group flex items-center justify-between rounded-lg px-3 py-2.5 cursor-pointer transition-colors',
                  selectedCatId === cat.catId
                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                    : 'text-slate-600 hover:bg-slate-50 border border-transparent'
                )}
              >
                <span className="text-sm font-medium truncate">{cat.catName}</span>
                {isAdmin && (
                  <button
                    onClick={(e) => handleDeleteCategory(cat.catId, e)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-600 transition-all shrink-0 ml-1"
                    title="분류 삭제"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* 분류 추가 버튼 (admin) */}
        {isAdmin && (
          <div className="border-t border-slate-200 p-3">
            <button
              onClick={() => setCatModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              분류 추가
            </button>
          </div>
        )}
      </div>

      {/* 우측 메인 */}
      <div className="flex-1 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden min-w-0">

        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50 shrink-0">
          <h3 className="text-lg font-bold text-slate-900 truncate">{currentCatName}</h3>
          {isAdmin && selectedCatId && (
            <button
              onClick={() => setPostModal({ open: true, mode: 'create', refId: null })}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors shrink-0 ml-4"
            >
              <PenLine className="h-4 w-4" />
              글쓰기
            </button>
          )}
        </div>

        {/* 게시판 */}
        <div className="overflow-y-auto max-h-[484px]">
          {!selectedCatId ? (
            <div className="flex h-full items-center justify-center text-slate-400 text-sm">
              좌측에서 분류를 선택하세요.
            </div>
          ) : isPostsLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-slate-400">
              등록된 게시글이 없습니다.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 sticky top-0">
                <tr>
                  <th className="w-14 py-3 text-center font-medium text-slate-500">번호</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-500">제목</th>
                  <th className="w-24 py-3 text-center font-medium text-slate-500">작성자</th>
                  <th className="w-28 py-3 text-center font-medium text-slate-500">등록일</th>
                  <th className="w-14 py-3 text-center font-medium text-slate-500">조회</th>
                  {isAdmin && <th className="w-12 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {posts.map((post, idx) => (
                  <tr key={post.refId} className="hover:bg-slate-50 transition-colors group">
                    <td className="py-3 text-center text-slate-400 text-xs">{page * PAGE_SIZE + idx + 1}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setPostModal({ open: true, mode: 'view', refId: post.refId })}
                        className="flex items-center gap-2 text-left w-full hover:text-blue-600 transition-colors"
                      >
                        <span className="font-medium text-slate-800 truncate">{post.title}</span>
                        {post.atchFileId && (
                          <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 text-center text-slate-500 text-xs">{post.regId}</td>
                    <td className="py-3 text-center text-slate-400 text-xs">
                      {post.regDtm?.substring(0, 10)}
                    </td>
                    <td className="py-3 text-center text-slate-400 text-xs">{post.viewCnt}</td>
                    {isAdmin && (
                      <td className="py-3 text-center">
                        <button
                          onClick={(e) => handleDeletePost(post.refId, e)}
                          className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-slate-300 transition-colors"
                          title="삭제"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 */}
       
          <div className="mt-auto border-t border-slate-200 px-6 py-3 flex items-center justify-between shrink-0">
            <span className="text-sm text-slate-500">총 {totalCount}건</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-2 py-1 rounded text-sm disabled:opacity-40 hover:bg-slate-100 transition-colors"
              >
                ‹
              </button>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={clsx(
                    'w-8 h-8 rounded text-sm transition-colors',
                    page === i ? 'bg-blue-600 text-white' : 'hover:bg-slate-100 text-slate-600'
                  )}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className="px-2 py-1 rounded text-sm disabled:opacity-40 hover:bg-slate-100 transition-colors"
              >
                ›
              </button>
            </div>
          </div>
        
      </div>

      {/* 분류 추가 모달 */}
      <RefCategoryModal
        open={catModalOpen}
        onClose={() => setCatModalOpen(false)}
        onSave={addCategory}
      />

      {/* 게시글 작성/상세/수정 모달 */}
      <ReferencePostModal
        open={postModal.open}
        mode={postModal.mode}
        refId={postModal.refId}
        selectedCatId={selectedCatId}
        onClose={() => setPostModal({ open: false, mode: 'create', refId: null })}
        onEdit={(refId) => setPostModal({ open: true, mode: 'edit', refId })}
        onSave={createPost}
        onUpdate={updatePost}
        onDeleteFile={deletePostFile}
        isAdmin={isAdmin}
      />
    </div>
  );
}
